// src/hooks/useEffectiveOrg.js
//
// Returns the effective active org for the current session — the
// impersonated org when a platform admin is impersonating (via
// usePlatformOrgContext), otherwise the user's real active org
// (via useOrgStore). Single source for any code that needs to
// reflect the post-impersonation org context.
//
// Shape: { id, name, slug?, is_service_provider, role?, ... } or null.
// Different paths populate slightly different fields, so callers
// should be defensive (e.g., `useEffectiveOrg()?.is_service_provider === true`).
//
// Pending state: when impersonation has just activated but the
// /orgs/active fetch hasn't resolved yet, the impersonated org
// object is null. This hook returns null in that window, NOT the
// user's real org (returning the wrong org during a pending state
// would briefly show stale chrome — better to fail closed).

import { useOrgStore } from "../stores/useOrgStore";
import { usePlatformOrgContext } from "../stores/usePlatformOrgContext";

export function useEffectiveOrg() {
  const impersonatedOrgId = usePlatformOrgContext((s) => s.activeOrgId);
  const impersonatedOrg   = usePlatformOrgContext((s) => s.activeOrg);
  const realOrg           = useOrgStore((s) => s.activeOrg);

  // Impersonating: return the impersonated org details if loaded; else
  // null (pending). Do NOT fall back to realOrg — that would show the
  // platform admin's home org while impersonation is mid-load.
  if (impersonatedOrgId) {
    return impersonatedOrg || null;
  }
  return realOrg || null;
}
